import { useState, useEffect } from 'react'
import { PlusIcon, MagnifyingGlassIcon, XMarkIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { norm } from '../utils/normalize'
import { useToast } from '../contexts/ToastContext'
import SortableHeader, { useSort } from '../components/SortableHeader'
import BrandsManager from '../components/BrandsManager'
import WorkUnitsManager from '../components/WorkUnitsManager'

const API_BASE_URL = '/api/v1/dev'

interface Allergen {
  id: number
  name: string
}

interface Unit {
  id: number
  name: string
  type: 'mass' | 'volume' | 'count'
}

interface Supplier {
  id: number
  trade_name: string
  nif: string
  is_self?: boolean
}

interface UnitWeight {
  id: number
  weight_g_per_unit: number
}

interface Ingredient {
  id: number
  name: string
  alias?: string | null
  display_name?: string
  type: 'normal' | 'elaborated' | 'limpieza' | 'packaging' | 'finished_product' | 'pack'
  default_unit_id: number
  default_unit_name: string
  preferred_supplier_id?: number
  preferred_supplier_name?: string
  default_price_per_unit?: number
  allergens: Allergen[]
  notes?: string
  density?: {
    id: number
    density_g_per_ml: number
  }
  unit_weights?: UnitWeight[]
}

interface FormData {
  name: string
  alias: string
  type: 'normal' | 'elaborated' | 'limpieza' | 'packaging' | 'finished_product' | 'pack'
  default_unit_id: number
  preferred_supplier_id: number
  default_price_per_unit: string
  notes: string
  allergen_ids: number[]
  density_g_per_ml: string
  weight_g_per_unit: string
}

export default function Ingredients() {
  const toast = useToast()
  const [ingredients, setIngredients] = useState<Ingredient[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [selfSupplier, setSelfSupplier] = useState<Supplier | null>(null)
  const [allergens, setAllergens] = useState<Allergen[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null)
  const [error, setError] = useState('')
  const [validationErrors, setValidationErrors] = useState<any>({})

  const [formData, setFormData] = useState<FormData>({
    name: '',
    alias: '',
    type: 'normal',
    default_unit_id: 0,
    preferred_supplier_id: 0,
    default_price_per_unit: '',
    notes: '',
    allergen_ids: [],
    density_g_per_ml: '',
    weight_g_per_unit: ''
  })

  const fetchIngredients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredients`, {
        headers: {
          'Accept': 'application/json'
        }
      })
      const data = await response.json()
      if (response.ok) {
        setIngredients(data.data)
      } else {
        setError('Error loading ingredients')
      }
    } catch (error) {
      setError('Connection error')
    }
  }

  const fetchUnits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/units`)
      const data = await response.json()
      if (response.ok) {
        setUnits(data.data)
      }
    } catch (error) {
      console.error('Error fetching units:', error)
    }
  }

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers`)
      const data = await response.json()
      if (response.ok) {
        setSuppliers(data.data.filter((s: any) => !s.is_self))
        const self = data.data.find((s: any) => s.is_self)
        if (self) {
          setSelfSupplier(self)
        }
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error)
    }
  }

  const fetchAllergens = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/allergens`)
      const data = await response.json()
      if (response.ok) {
        setAllergens(data.data)
      }
    } catch (error) {
      console.error('Error fetching allergens:', error)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchIngredients(),
        fetchUnits(),
        fetchSuppliers(),
        fetchAllergens()
      ])
      setLoading(false)
    }
    loadData()
  }, [])

  // Helper function to check if a unit is volume-based
  const isVolumeUnit = (unitId: number) => {
    const unit = units.find(u => u.id === unitId)
    return unit?.type === 'volume'
  }

  // Helper function to check if a unit is count-based
  const isCountUnit = (unitId: number) => {
    const unit = units.find(u => u.id === unitId)
    return unit?.type === 'count'
  }

  // Function to handle density creation/update for ingredients
  const handleDensityForIngredient = async (ingredientId: number, densityValue: number) => {
    try {
      // Check if density already exists
      const existingDensityResponse = await fetch(`${API_BASE_URL}/densities?ingredient_id=${ingredientId}`)
      const existingDensityData = await existingDensityResponse.json()

      if (existingDensityData.data && existingDensityData.data.length > 0) {
        // Update existing density
        const densityId = existingDensityData.data[0].id
        await fetch(`${API_BASE_URL}/densities/${densityId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ density_g_per_ml: densityValue })
        })
      } else {
        // Create new density
        await fetch(`${API_BASE_URL}/densities`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ingredient_id: ingredientId,
            density_g_per_ml: densityValue
          })
        })
      }
    } catch (error) {
      console.error('Error handling density:', error)
    }
  }

  // Function to handle unit weight creation/update for ingredients
  const handleUnitWeightForIngredient = async (ingredientId: number, unitId: number, weightValue: number) => {
    try {
      // Check if unit weight already exists
      const existingUnitWeightResponse = await fetch(`${API_BASE_URL}/unit-weights?ingredient_id=${ingredientId}`)
      const existingUnitWeightData = await existingUnitWeightResponse.json()

      const existingUnitWeight = existingUnitWeightData.data?.find((uw: any) => uw.unit_id === unitId)

      if (existingUnitWeight) {
        // Update existing unit weight
        await fetch(`${API_BASE_URL}/unit-weights/${existingUnitWeight.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ weight_g_per_unit: weightValue })
        })
      } else {
        // Create new unit weight
        await fetch(`${API_BASE_URL}/unit-weights`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ingredient_id: ingredientId,
            unit_id: unitId,
            weight_g_per_unit: weightValue
          })
        })
      }
    } catch (error) {
      console.error('Error handling unit weight:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setValidationErrors({})

    // Validate density for volume units
    if (isVolumeUnit(formData.default_unit_id) && !formData.density_g_per_ml) {
      setError('La densidad es requerida para ingredientes con unidades de volumen')
      return
    }

    // Validate unit weight for count units (except packaging which auto-zeros)
    if (isCountUnit(formData.default_unit_id) && formData.type !== 'packaging' && !formData.weight_g_per_unit) {
      setError('El peso por unidad es requerido para ingredientes con unidades de conteo')
      return
    }

    const body = {
      name: formData.name,
      alias: formData.alias.trim() || null,
      type: formData.type,
      default_unit_id: formData.default_unit_id,
      preferred_supplier_id: formData.preferred_supplier_id || null,
      default_price_per_unit: formData.default_price_per_unit ? parseFloat(formData.default_price_per_unit) : null,
      notes: formData.notes || null,
      allergen_ids: formData.allergen_ids
    }

    try {
      const url = editingIngredient
        ? `${API_BASE_URL}/ingredients/${editingIngredient.id}`
        : `${API_BASE_URL}/ingredients`

      const response = await fetch(url, {
        method: editingIngredient ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (response.ok) {
        const savedIngredient = data.data

        // Handle density for volume units
        if (isVolumeUnit(formData.default_unit_id) && formData.density_g_per_ml) {
          await handleDensityForIngredient(savedIngredient.id, parseFloat(formData.density_g_per_ml))
        }

        // Handle unit weight for count units
        if (isCountUnit(formData.default_unit_id)) {
          const weightValue = formData.type === 'packaging' ? 0 : parseFloat(formData.weight_g_per_unit)
          await handleUnitWeightForIngredient(savedIngredient.id, formData.default_unit_id, weightValue)
        }

        // Reload ingredients to get updated density/unit weight info
        await fetchIngredients()
        toast.success(editingIngredient ? 'Ingrediente actualizado' : 'Ingrediente creado')
        handleCloseModal()
      } else {
        if (data.errors) {
          setValidationErrors(data.errors)
          const msgs = Object.values(data.errors).flat().join(', ')
          toast.error(msgs)
        } else {
          const msg = data.message || 'Error saving ingredient'
          setError(msg)
          toast.error(msg)
        }
      }
    } catch (error) {
      toast.error('Error de conexión con el servidor')
    }
  }

  const handleEdit = (ingredient: Ingredient) => {
    setEditingIngredient(ingredient)

    // Find unit weight for the default unit
    const unitWeight = ingredient.unit_weights?.find(uw => uw.id === ingredient.default_unit_id)

    // For elaborated, finished_product and pack types, always use self-supplier
    const supplierId = (ingredient.type === 'elaborated' || ingredient.type === 'finished_product' || ingredient.type === 'pack') && selfSupplier
      ? selfSupplier.id
      : (ingredient.preferred_supplier_id || 0)

    // For pack and packaging types, ensure unit is "unidad"
    let unitId = ingredient.default_unit_id
    if (ingredient.type === 'pack' || ingredient.type === 'packaging') {
      const unidadUnit = units.find(u => u.name.toLowerCase() === 'unidad')
      if (unidadUnit) unitId = unidadUnit.id
    }

    setFormData({
      name: ingredient.name,
      alias: ingredient.alias || '',
      type: ingredient.type,
      default_unit_id: unitId,
      preferred_supplier_id: supplierId,
      default_price_per_unit: ingredient.default_price_per_unit?.toString() || '',
      notes: ingredient.notes || '',
      allergen_ids: ingredient.allergens.map(a => a.id),
      density_g_per_ml: ingredient.density?.density_g_per_ml?.toString() || '',
      weight_g_per_unit: unitWeight?.weight_g_per_unit?.toString() || ''
    })
    setShowAddModal(true)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Está seguro de que desea eliminar este ingrediente?')) {
      return
    }

    setError('') // Clear any previous errors

    try {
      const response = await fetch(`${API_BASE_URL}/ingredients/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setIngredients(ingredients.filter(ingredient => ingredient.id !== id))
        toast.success('Ingrediente eliminado')
      } else {
        const data = await response.json()
        const msg = data.message || 'Error deleting ingredient'
        setError(msg)
        toast.error(msg)
      }
    } catch (error) {
      setError('Connection error')
      toast.error('Error de conexión con el servidor')
    }
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setEditingIngredient(null)
    setFormData({
      name: '',
      alias: '',
      type: 'normal',
      default_unit_id: 0,
      preferred_supplier_id: 0,
      default_price_per_unit: '',
      notes: '',
      allergen_ids: [],
      density_g_per_ml: '',
      weight_g_per_unit: ''
    })
    setError('')
    setValidationErrors({})
  }

  const handleAllergenToggle = (allergenId: number) => {
    setFormData(prev => ({
      ...prev,
      allergen_ids: prev.allergen_ids.includes(allergenId)
        ? prev.allergen_ids.filter(id => id !== allergenId)
        : [...prev.allergen_ids, allergenId]
    }))
  }

  const [currentPage, setCurrentPage] = useState(1)
  const perPage = 15
  const { sortField, sortDirection, handleSort, sortData } = useSort<Ingredient>('name')

  const filteredIngredients = ingredients.filter(ingredient => {
    const matchesSearch = norm(ingredient.name).includes(norm(searchTerm))
    const matchesType = typeFilter === 'all' || ingredient.type === typeFilter
    return matchesSearch && matchesType
  })

  const sortedIngredients = sortData(filteredIngredients)
  const totalPages = Math.ceil(filteredIngredients.length / perPage)
  const paginatedIngredients = sortedIngredients.slice((currentPage - 1) * perPage, currentPage * perPage)

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, typeFilter])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Ingredientes</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestión de ingredientes básicos y elaborados con unidades, proveedores y densidades
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Nuevo Ingrediente
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar ingredientes..."
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">Todos los tipos</option>
              <option value="normal">Básicos</option>
              <option value="elaborated">Elaborados</option>
              <option value="finished_product">Productos Terminados</option>
              <option value="pack">Packs</option>
              <option value="limpieza">Productos de Limpieza</option>
              <option value="packaging">Packaging</option>
            </select>
          </div>
        </div>
      </div>

      {/* Info about volume units and densities */}
      {filteredIngredients.some(ingredient => ingredient.default_unit_name === 'litro' || ingredient.default_unit_name === 'ml') && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-blue-400 text-lg">🌡️</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Ingredientes con unidades de volumen:</strong> Los ingredientes que usan litros o ml necesitan
                densidades definidas para poder convertirse a gramos. Las densidades se gestionan en la sección
                "Densidades" y son esenciales para el cálculo de recetas.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ingredients Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader label="Nombre" field="name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
              <SortableHeader label="Tipo" field="type" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} className="whitespace-nowrap" />
              <SortableHeader label="Unidad" field="default_unit_name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} className="whitespace-nowrap" />
              <SortableHeader label="Proveedor" field="preferred_supplier_name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
              <SortableHeader label="Precio" field="default_price_per_unit" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} className="whitespace-nowrap" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Densidad
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Alérgenos
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedIngredients.map((ingredient) => (
              <tr key={ingredient.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">
                    {ingredient.name}
                  </div>
                  {ingredient.alias && (
                    <div className="text-xs text-purple-600 italic">alias: {ingredient.alias}</div>
                  )}
                  {ingredient.notes && (
                    <div className="text-xs text-gray-500">{ingredient.notes}</div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    ingredient.type === 'normal' ? 'bg-green-100 text-green-800' :
                    ingredient.type === 'elaborated' ? 'bg-purple-100 text-purple-800' :
                    ingredient.type === 'finished_product' ? 'bg-pink-100 text-pink-800' :
                    ingredient.type === 'pack' ? 'bg-indigo-100 text-indigo-800' :
                    ingredient.type === 'limpieza' ? 'bg-blue-100 text-blue-800' :
                    'bg-orange-100 text-orange-800'
                  }`}>
                    {ingredient.type === 'normal' ? 'Básico' :
                     ingredient.type === 'elaborated' ? 'Elaborado' :
                     ingredient.type === 'finished_product' ? 'Producto Terminado' :
                     ingredient.type === 'pack' ? 'Pack' :
                     ingredient.type === 'limpieza' ? 'Limpieza' :
                     'Packaging'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {ingredient.default_unit_name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {ingredient.preferred_supplier_name || 'Sin proveedor'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {ingredient.default_price_per_unit ? `€${ingredient.default_price_per_unit}` : '-'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {ingredient.default_unit_name === 'litro' || ingredient.default_unit_name === 'ml' ? (
                    ingredient.density ? (
                      <span className="text-sm text-green-600 font-medium">
                        {parseFloat(ingredient.density.density_g_per_ml.toString()).toFixed(3)} g/ml
                      </span>
                    ) : (
                      <span className="text-sm text-red-600 font-medium">Sin densidad</span>
                    )
                  ) : (
                    <span className="text-gray-400 text-sm">N/A</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {ingredient.type === 'limpieza' || ingredient.type === 'packaging' || ingredient.type === 'pack' ? (
                    <span className="text-sm text-gray-400">N/A</span>
                  ) : ingredient.allergens.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {ingredient.allergens.map((allergen) => (
                        <span
                          key={allergen.id}
                          className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"
                        >
                          {allergen.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Sin alérgenos</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEdit(ingredient)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(ingredient.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Página {currentPage} de {totalPages} ({filteredIngredients.length} ingredientes)
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingIngredient ? 'Editar Ingrediente' : 'Nuevo Ingrediente'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                  {validationErrors.name && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.name[0]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => {
                      const newType = e.target.value as 'normal' | 'elaborated' | 'limpieza' | 'packaging' | 'finished_product' | 'pack'
                      // Auto-select self supplier for elaborated, finished_product, and pack types
                      if ((newType === 'elaborated' || newType === 'finished_product' || newType === 'pack') && selfSupplier) {
                        // For pack type, also auto-select "unidad" as default unit
                        if (newType === 'pack') {
                          const unidadUnit = units.find(u => u.name.toLowerCase() === 'unidad')
                          setFormData({
                            ...formData,
                            type: newType,
                            preferred_supplier_id: selfSupplier.id,
                            default_unit_id: unidadUnit?.id || formData.default_unit_id
                          })
                        } else {
                          setFormData({ ...formData, type: newType, preferred_supplier_id: selfSupplier.id })
                        }
                      } else if (newType === 'packaging') {
                        // For packaging, auto-select "unidad" as default unit
                        const unidadUnit = units.find(u => u.name.toLowerCase() === 'unidad')
                        setFormData({
                          ...formData,
                          type: newType,
                          default_unit_id: unidadUnit?.id || formData.default_unit_id
                        })
                      } else {
                        setFormData({ ...formData, type: newType })
                      }
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="normal">Básico</option>
                    <option value="elaborated">Elaborado</option>
                    <option value="finished_product">Producto Terminado</option>
                    <option value="pack">Pack</option>
                    <option value="limpieza">Producto de Limpieza</option>
                    <option value="packaging">Packaging</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alias (nombre para etiquetas y fichas)
                  </label>
                  <input
                    type="text"
                    value={formData.alias}
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder={formData.name || 'Si se deja vacío se usa el nombre real'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Nombre con el que aparece en etiquetas y fichas técnicas. No tiene que ser único; si lo dejas vacío se usa el nombre real.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidad por defecto *
                  </label>
                  {(formData.type === 'pack' || formData.type === 'packaging') ? (
                    <div>
                      <input
                        type="text"
                        value="unidad"
                        disabled
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.type === 'pack' ? 'Los packs' : 'El packaging'} siempre usa "unidad" como unidad por defecto
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.default_unit_id}
                      onChange={(e) => setFormData({ ...formData, default_unit_id: parseInt(e.target.value) })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value={0}>Seleccionar unidad</option>
                      {units.map(unit => (
                        <option key={unit.id} value={unit.id}>{unit.name}</option>
                      ))}
                    </select>
                  )}
                  {validationErrors.default_unit_id && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.default_unit_id[0]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proveedor preferido
                  </label>
                  {(formData.type === 'elaborated' || formData.type === 'finished_product' || formData.type === 'pack') ? (
                    <div>
                      <input
                        type="text"
                        value={selfSupplier?.trade_name || 'Negocio Propio'}
                        disabled
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Los ingredientes elaborados y productos terminados se asignan automáticamente al negocio propio
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.preferred_supplier_id}
                      onChange={(e) => setFormData({ ...formData, preferred_supplier_id: parseInt(e.target.value) })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value={0}>Sin proveedor</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>{supplier.trade_name}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio por unidad (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.default_price_per_unit}
                    onChange={(e) => setFormData({ ...formData, default_price_per_unit: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                  {validationErrors.default_price_per_unit && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.default_price_per_unit[0]}</p>
                  )}
                </div>
              </div>

              {/* Density field - only show for volume units */}
              {isVolumeUnit(formData.default_unit_id) && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Densidad (g/ml) *
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0.001"
                    max="50"
                    value={formData.density_g_per_ml}
                    onChange={(e) => setFormData({ ...formData, density_g_per_ml: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="ej: 1.030 para leche"
                    required
                  />
                  <p className="text-blue-600 text-xs mt-1">
                    Requerida para ingredientes con unidades de volumen. Define cuántos gramos pesa 1ml del ingrediente.
                  </p>
                  {validationErrors.density_g_per_ml && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.density_g_per_ml[0]}</p>
                  )}
                </div>
              )}

              {/* Unit weight field - only show for count units */}
              {isCountUnit(formData.default_unit_id) && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <label className="block text-sm font-medium text-green-700 mb-1">
                    Peso por unidad (gramos) {formData.type !== 'packaging' && '*'}
                  </label>
                  {formData.type === 'packaging' ? (
                    <div>
                      <input
                        type="number"
                        value="0"
                        disabled
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500"
                      />
                      <p className="text-green-600 text-xs mt-1">
                        Los ingredientes de tipo packaging tienen peso 0 gramos automáticamente.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="number"
                        step="0.001"
                        min="0.001"
                        value={formData.weight_g_per_unit}
                        onChange={(e) => setFormData({ ...formData, weight_g_per_unit: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="ej: 55 para un huevo, 660 para una docena"
                        required
                      />
                      <p className="text-green-600 text-xs mt-1">
                        Requerida para ingredientes con unidades de conteo. Define cuántos gramos pesa una unidad del ingrediente.
                      </p>
                    </div>
                  )}
                  {validationErrors.weight_g_per_unit && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.weight_g_per_unit[0]}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Notas adicionales sobre el ingrediente..."
                />
              </div>

              {formData.type !== 'limpieza' && formData.type !== 'packaging' && formData.type !== 'pack' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alérgenos
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                    {allergens.map(allergen => (
                      <label key={allergen.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.allergen_ids.includes(allergen.id)}
                          onChange={() => handleAllergenToggle(allergen.id)}
                          className="mr-2"
                        />
                        <span className="text-sm">{allergen.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {editingIngredient && (
                <div className="border-t border-gray-200 pt-4 space-y-5">
                  <BrandsManager ingredientId={editingIngredient.id} allergens={allergens} onError={(m) => toast.error(m)} />
                  <WorkUnitsManager
                    ingredientId={editingIngredient.id}
                    units={units}
                    defaultUnitId={formData.default_unit_id}
                    onError={(m) => toast.error(m)}
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  {editingIngredient ? 'Actualizar' : 'Crear'} Ingrediente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}